import { getAuth } from "firebase/auth";

const API_URL = process.env.REACT_APP_API_URL;

async function getAuthHeaders() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export async function importCSV({ orgId, records, source, industry, geography }) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/insights/import/csv`, {
    method: "POST",
    headers,
    body: JSON.stringify({ orgId, records, source, industry, geography }),
  });
  if (!res.ok) throw new Error((await res.json()).error || "Import failed");
  return res.json();
}

export async function importGooglePlay({ orgId, packageName, industry, geography }) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/insights/import/google-play`, {
    method: "POST",
    headers,
    body: JSON.stringify({ orgId, packageName, industry, geography }),
  });
  if (!res.ok) throw new Error((await res.json()).error || "Import failed");
  return res.json();
}

export async function getSummary(orgId) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/insights/summary?orgId=${orgId}`, { headers });
  if (!res.ok) throw new Error((await res.json()).error || "Failed to load summary");
  return res.json();
}

export async function getFeedback(orgId, filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ orgId, ...filters });
  const res = await fetch(`${API_URL}/api/insights/feedback?${params}`, { headers });
  if (!res.ok) throw new Error((await res.json()).error || "Failed to load feedback");
  return res.json();
}

export async function askQuestion({ orgId, question, industry, geography }) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/insights/query`, {
    method: "POST",
    headers,
    body: JSON.stringify({ orgId, question, industry, geography }),
  });
  if (!res.ok) throw new Error((await res.json()).error || "Query failed");
  return res.json();
}

export async function askQuestionStream({ orgId, question, industry, geography }, onChunk, onMeta) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/insights/query/stream`, {
    method: "POST",
    headers,
    body: JSON.stringify({ orgId, question, industry, geography }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Stream failed" }));
    throw new Error(err.error || "Query failed");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      try {
        const data = JSON.parse(line.slice(6));
        if (data.done) return;
        if (data.error) throw new Error(data.error);
        if (data.dataSize && onMeta) onMeta(data);
        if (data.text) onChunk(data.text);
      } catch (e) {
        if (e.message && e.message !== "Unexpected end of JSON input") throw e;
      }
    }
  }
}
